import React from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker'
import { pickersLayoutClasses } from '@mui/x-date-pickers/PickersLayout'
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'

import Dayjs from 'dayjs'
import 'dayjs/locale/sv'
import { SxProps } from '@mui/material'
import { useTranslation } from 'language/translator'

interface DatePickerProps {
  clearable?: boolean
  label?: string
  maxDate?: string
  minDate?: string
  onChange: (value: string | null) => void
  value: string | null
  views: Array<'day' | 'month' | 'year'>
  slotProps?: object
  size?: 'small' | 'medium'
  sx?: SxProps
  popperSx?: SxProps
}

function ActionBar(props: PickersActionBarProps) {
  const { className, onClear, ref } = props

  const { t } = useTranslation()

  return (
    <Box className={className} ref={ref}>
      <Button variant="text" onClick={onClear}>
        {t('global', 'clear')}
      </Button>
    </Box>
  )
}

const DatePicker = (props: DatePickerProps) => {
  const {
    clearable = false,
    label,
    maxDate,
    minDate,
    onChange,
    value,
    size = 'small',
    popperSx,
    ...rest
  } = props

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'sv'}>
      <MUIDatePicker
        {...rest}
        label={label}
        // The public onChange keeps the legacy loose typing: at runtime the
        // picker has always emitted a Dayjs object and consumers re-parse it.
        onChange={(newValue) => onChange(newValue as unknown as string | null)}
        value={value ? Dayjs(value) : null}
        minDate={minDate ? Dayjs(minDate) : undefined}
        maxDate={maxDate ? Dayjs(maxDate) : undefined}
        slotProps={{
          popper: {
            sx: popperSx
          },
          textField: { size },
          layout: {
            sx: {
              [`.${pickersLayoutClasses.actionBar}`]: {
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'end',
                mb: 1
              }
            }
          }
        }}
        slots={
          clearable
            ? {
                actionBar: (slotsProps) => (
                  <ActionBar {...slotsProps} onClear={() => onChange(null)} />
                )
              }
            : undefined
        }
      />
    </LocalizationProvider>
  )
}

DatePicker.displayName = 'DatePicker'

export default DatePicker
